import { componentSizes } from '@/theme/componentSizes';
import { r, s } from '@/theme/tokens';
import { Box, Skeleton, Table, TableBody, TableCell, TableHead, TableRow } from '@/ui/primitives';
import type { ReactElement } from 'react';

type EntityDailyHistoryPanelSkeletonProps = {
  dayCount: number;
  rowCount: number;
  nameColumnWidth: string;
  rowHeight: number;
  blockSize: number;
  headerHeight: number;
};

const HISTORY_SKELETON_HEIGHT = 14;
const HISTORY_SKELETON_RADIUS = '999px';
const HISTORY_STATUS_BLOCK_SKELETON_HEIGHT = 14;
const HISTORY_SKELETON_ROW_MIN_HEIGHT_OFFSET = 12;
const HEADER_NAME_WIDTH = '76px';
const HEADER_STATUS_WIDTH = '84px';
const HEADER_DATE_WIDTH = '38px';
const FOOTER_SKELETON_WIDTH = '220px';
const ROW_NAME_WIDTHS = ['72%', '64%', '68%', '70%', '62%'] as const;

export const EntityDailyHistoryPanelSkeleton = ({
  dayCount,
  rowCount,
  nameColumnWidth,
  rowHeight,
  blockSize,
  headerHeight,
}: EntityDailyHistoryPanelSkeletonProps): ReactElement => (
  <>
    <Table
      aria-label="Loading history"
      sx={{
        tableLayout: 'auto',
        width: '100%',
        borderCollapse: 'separate',
        borderSpacing: 0,
      }}
    >
      <TableHead>
        <TableRow
          sx={{
            height: `${headerHeight}px`,
          }}
        >
          <TableCell
            sx={{
              width: nameColumnWidth,
              minWidth: nameColumnWidth,
              maxWidth: nameColumnWidth,
              px: s('md'),
              pt: s('lg'),
              verticalAlign: 'top',
              borderBottom: '1px solid',
              borderColor: 'divider',
              position: 'relative',
              '&::after': {
                content: '""',
                position: 'absolute',
                right: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                width: '1px',
                height: `calc(${headerHeight}px - ${s('2xl')})`,
                backgroundColor: 'divider',
              },
            }}
          >
            <Skeleton
              variant="rounded"
              width={HEADER_NAME_WIDTH}
              sx={{
                height: `${HISTORY_SKELETON_HEIGHT}px`,
                transform: 'none',
                borderRadius: HISTORY_SKELETON_RADIUS,
              }}
            />
          </TableCell>
          <TableCell
            sx={{
              px: s('md'),
              pt: s('lg'),
              pb: '4px',
              verticalAlign: 'top',
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: s('md'), minWidth: 0 }}>
              <Skeleton
                variant="rounded"
                width={HEADER_STATUS_WIDTH}
                sx={{
                  height: `${HISTORY_SKELETON_HEIGHT}px`,
                  transform: 'none',
                  borderRadius: HISTORY_SKELETON_RADIUS,
                }}
              />
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(${String(dayCount)}, minmax(0, 1fr))`,
                  columnGap: s('md'),
                  alignItems: 'center',
                  width: '100%',
                  minWidth: 0,
                }}
              >
                {Array.from({ length: dayCount }, (_unused, index) => (
                  <Skeleton
                    key={`history-skeleton-header-day-${String(index)}`}
                    variant="rounded"
                    width={HEADER_DATE_WIDTH}
                    sx={{
                      height: `${HISTORY_SKELETON_HEIGHT}px`,
                      transform: 'none',
                      borderRadius: HISTORY_SKELETON_RADIUS,
                      justifySelf: 'start',
                    }}
                  />
                ))}
              </Box>
            </Box>
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {Array.from({ length: rowCount }, (_unused, rowIndex) => (
          <TableRow
            key={`history-skeleton-row-${String(rowIndex)}`}
            sx={{
              minHeight: `${rowHeight + HISTORY_SKELETON_ROW_MIN_HEIGHT_OFFSET}px`,
            }}
          >
            <TableCell
              sx={[
                {
                  width: nameColumnWidth,
                  minWidth: nameColumnWidth,
                  maxWidth: nameColumnWidth,
                  px: s('md'),
                  py: s('md'),
                  verticalAlign: 'middle',
                  borderBottom: 'none',
                },
                rowIndex === 0 ? { pt: `calc(${s('xl')} + 2px)` } : null,
                rowIndex === rowCount - 1 ? { pb: `calc(${s('xl')} + 1.5px)` } : null,
              ]}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: s('sm'), minWidth: 0 }}>
                <Skeleton
                  variant="rounded"
                  width={ROW_NAME_WIDTHS[rowIndex % ROW_NAME_WIDTHS.length]}
                  sx={{
                    height: `${HISTORY_SKELETON_HEIGHT}px`,
                    transform: 'none',
                    borderRadius: HISTORY_SKELETON_RADIUS,
                    flexShrink: 0,
                  }}
                />
              </Box>
            </TableCell>
            <TableCell
              sx={[
                {
                  px: s('md'),
                  py: s('md'),
                  verticalAlign: 'middle',
                  borderBottom: 'none',
                  width: '100%',
                },
                rowIndex === 0 ? { pt: `calc(${s('lg')} + 2px)` } : null,
                rowIndex === rowCount - 1 ? { pb: `calc(${s('lg')} + 2px)` } : null,
              ]}
            >
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: `repeat(${String(dayCount)}, minmax(0, 1fr))`,
                  columnGap: s('md'),
                  alignItems: 'center',
                  width: '100%',
                  minWidth: 0,
                }}
              >
                {Array.from({ length: dayCount }, (_dayValue, dayIndex) => (
                  <Skeleton
                    key={`history-skeleton-cell-${String(rowIndex)}-${String(dayIndex)}`}
                    variant="rounded"
                    sx={{
                      width: '100%',
                      minWidth: `${blockSize}px`,
                      height: `${HISTORY_STATUS_BLOCK_SKELETON_HEIGHT}px`,
                      transform: 'none',
                      borderRadius: r('sm'),
                    }}
                  />
                ))}
              </Box>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        width: '100%',
        height: `${componentSizes.dataGrid.footerHeight}px`,
        minHeight: `${componentSizes.dataGrid.footerHeight}px`,
        maxHeight: `${componentSizes.dataGrid.footerHeight}px`,
        boxSizing: 'border-box',
        px: s('md'),
        py: s('none'),
        borderTop: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Skeleton
        variant="rounded"
        width={FOOTER_SKELETON_WIDTH}
        sx={{
          height: `${HISTORY_SKELETON_HEIGHT}px`,
          transform: 'none',
          borderRadius: HISTORY_SKELETON_RADIUS,
        }}
      />
    </Box>
  </>
);
